import { Link } from "react-router-dom";
import {
  ArrowRight,
  Eye,
  ShieldCheck,
  FileText,
  Database,
} from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";

const benefits = [
  {
    icon: Eye,
    title: "Meer zicht op leveranciersafhankelijkheid",
    text:
      "Organisaties krijgen inzicht in welke leveranciers cruciaal zijn voor hun digitale functioneren en waar overstappen moeilijk is.",
  },
  {
    icon: ShieldCheck,
    title: "Betere bestuurlijke verantwoording",
    text:
      "Bestuurders kunnen uitleggen waarom voor bepaalde systemen is gekozen en welke alternatieven zijn overwogen.",
  },
  {
    icon: FileText,
    title: "Sterkere positie bij inkoop en contractverlenging",
    text:
      "Europese alternatieven kunnen worden meegenomen in aanbestedingen, marktverkenningen en leveranciersgesprekken.",
  },
  {
    icon: Database,
    title: "Meer grip op data en continuïteit",
    text:
      "De organisatie krijgt beter zicht op waar data staat, wie invloed heeft op de voorwaarden en welke keuzes beschikbaar zijn.",
  },
];

const considerations = [
  "Is er een Europees alternatief?",
  "Past dat alternatief functioneel bij onze organisatie?",
  "Geeft het meer zeggenschap over data?",
  "Vermindert het juridische of bestuurlijke afhankelijkheid?",
  "Welke bestuurlijke risico's vermindert of vergroot deze keuze?",
];

export default function DigitaleRegie() {
  return (
    <div data-testid="page-digitale-regie" className="pt-[96px]">
      <SeoHead
        title="Digitale regie — SovEU"
        description="SovEU helpt organisaties van compliance naar controle en van afhankelijkheid naar regie, met aandacht voor Europese digitale alternatieven en datagovernance."
        path="/digitale-regie"
      />

      {/* ===== Hero ===== */}
      <section className="section bg-white" data-testid="dr-hero">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl">
            <div className="eyebrow mb-4">Strategisch thema</div>
            <h1 className="h1-display mb-6">Digitale afhankelijkheid is een bestuurlijke keuze</h1>
            <p
              className="text-[18px] md:text-[20px] font-medium leading-snug mb-7"
              style={{ color: "#2D6F8F" }}
            >
              Van compliance naar controle. Van afhankelijkheid naar regie.
            </p>
            <p className="body-lg text-[#334155]">
              <strong style={{ color: "#1E3A5F" }}>Europa digitaal meer op eigen benen.</strong>{" "}
              Publieke en semipublieke organisaties zijn sterk afhankelijk geworden van digitale dienstverlening van bedrijven die buiten Europa zijn gevestigd en onder niet Europese wetgeving vallen. De data staat vaak fysiek wel in Europa, maar de juridische zeggenschap over die data en de voorwaarden waaronder toegang kan worden gevorderd, ligt buiten de EU.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== Sectie 1: Waarom dit nu speelt ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="dr-why"
      >
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <h2 className="h2-display mb-6">
              Digitale afhankelijkheid is een bestuurlijk vraagstuk
            </h2>
          </FadeIn>
          <FadeIn delay={100} className="space-y-5 body-lg text-[#334155] leading-relaxed">
            <p>
              De vraag is niet alleen of een systeem technisch goed werkt. De vraag is ook wie de voorwaarden bepaalt, onder welk rechtsstelsel data valt, hoe afhankelijk de organisatie is van één leverancier en wat er gebeurt als voorwaarden, prijzen of toegang veranderen.
            </p>
            <p>
              Voor publieke organisaties raakt dit direct aan vertrouwen. Burgers, huurders, leerlingen, patiënten en cliënten moeten erop kunnen rekenen dat hun gegevens zorgvuldig worden beheerd en dat organisaties kunnen uitleggen welke keuzes zij maken.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== Sectie 2: De keuze wordt groter ===== */}
      <section className="section bg-white" data-testid="dr-options">
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <h2 className="h2-display mb-6">Europese alternatieven worden serieuzer</h2>
          </FadeIn>
          <FadeIn delay={100} className="space-y-5 body-lg text-[#334155] leading-relaxed">
            <p>
              In steeds meer digitale categorieën bestaan Europese alternatieven die organisaties kunnen meenemen in hun afweging. Denk aan cloudopslag, e mail, videoconferencing, documentbeheer, samenwerkingstools en bepaalde AI toepassingen.
            </p>
            <p>
              Dat betekent niet dat elke organisatie morgen volledig moet overstappen. Het betekent wel dat bij nieuwe aanbestedingen, contractverlengingen en toolkeuzes bewust de vraag gesteld kan worden:
            </p>
          </FadeIn>

          <FadeIn delay={200}>
            <ul className="mt-7 space-y-3">
              {considerations.map((q, i) => (
                <li
                  key={q}
                  className="soveu-card p-5 flex items-start gap-4"
                  data-testid={`dr-question-${i + 1}`}
                >
                  <span
                    className="flex-shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center text-[14px] font-semibold"
                    style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[#334155] text-[15px] leading-relaxed pt-1">
                    {q}
                  </span>
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* ===== Sectie 3: Wat SovEU doet ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="dr-approach"
      >
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <h2 className="h2-display mb-6">Van losse keuze naar structurele regie</h2>
          </FadeIn>
          <FadeIn delay={100} className="space-y-5 body-lg text-[#334155] leading-relaxed">
            <p>
              SovEU helpt organisaties deze afweging structureel te maken. Niet vanuit ideologie, maar vanuit bestuurlijke controle. Europese digitale regie betekent dat een organisatie bewust weet welke systemen zij gebruikt, welke afhankelijkheden daarbij ontstaan en welke alternatieven realistisch zijn.
            </p>
            <p>
              SovEU brengt dit in beeld binnen de bredere analyse van AI gebruik, algoritmen, cloud afhankelijkheden, datastromen en leveranciersmacht.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== Sectie 4: Wat het oplevert ===== */}
      <section className="section bg-white" data-testid="dr-benefits">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display mb-5">Wat digitale regie oplevert</h2>
            <p className="body-lg text-[#334155]">
              Concreet maakt digitale regie een organisatie weerbaarder, transparanter en beter voorbereid op bestuurlijke en operationele veranderingen.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((b, i) => (
              <FadeIn key={b.title} delay={i * 80}>
                <article
                  className="soveu-card p-7 h-full"
                  data-testid={`dr-benefit-${i + 1}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-md inline-flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                    >
                      <b.icon size={22} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1">
                      <h3 className="h3-display mb-3">{b.title}</h3>
                      <p className="text-[#334155] leading-relaxed text-[15px]">
                        {b.text}
                      </p>
                    </div>
                  </div>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Sectie 5: Belangrijke nuance ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="dr-nuance"
      >
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <h2 className="h2-display mb-6">
              Geen automatische keuze, wel een bewuste afweging
            </h2>
          </FadeIn>
          <FadeIn delay={100} className="space-y-5 body-lg text-[#334155] leading-relaxed">
            <p>
              SovEU stelt niet dat Europese alternatieven altijd de beste keuze zijn. Soms is een bestaande oplossing functioneel, financieel of organisatorisch voorlopig logischer. Maar een organisatie die haar digitale positie serieus neemt, moet die keuze bewust kunnen maken en uitleggen.
            </p>
            <p className="font-medium" style={{ color: "#1E3A5F" }}>
              Europese digitale regie begint niet met migreren. Het begint met overzicht.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#1E3A5F" }}
        data-testid="dr-cta"
      >
        <div className="container-soveu max-w-3xl text-center text-white">
          <FadeIn>
            <h2 className="h2-display text-white mb-5">
              Wilt u weten waar uw organisatie digitaal afhankelijk is?
            </h2>
            <p className="body-lg text-white/75 mb-9">
              Met een Bestuursscan of Regiebeeld brengt SovEU in kaart welke systemen, leveranciers en datastromen bestuurlijke aandacht vragen en waar Europese alternatieven relevant kunnen zijn.
            </p>
            <Link
              to="/contact"
              className="btn-primary btn-on-navy"
              data-testid="dr-cta-button"
            >
              Plan een vrijblijvend gesprek
              <ArrowRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
